/**
 * RelatedObjectsChips — groups an object's relationshipsFor() edges by kind
 * into labeled sections, rendering one ObjectChip per edge. Group order
 * follows ObjectKind's declaration order in KIND_META (entity, surface,
 * cloud-function, event-type, firestore-path, feature, flag, hub,
 * statemachine) — stable regardless of edge array order.
 *
 * @see docs/superpowers/specs/2026-08-10-atlas-feature-flag-relationships-design.md Part 3
 */
import type { JSX } from 'react';
import { ObjectChip } from './ObjectChip.js';
import { KIND_META, type ObjectKind, type RelationEdge } from '../state/objectKinds.js';

export interface RelatedObjectsChipsProps {
  edges: RelationEdge[];
  onNavigate: (kind: ObjectKind, id: string) => void;
}

/** "Entity" -> "Entities"; every other KIND_META label just takes a trailing "s". */
function pluralize(label: string): string {
  return label.endsWith('y') ? `${label.slice(0, -1)}ies` : `${label}s`;
}

export function RelatedObjectsChips({ edges, onNavigate }: RelatedObjectsChipsProps): JSX.Element | null {
  if (edges.length === 0) return null;

  const kindOrder = Object.keys(KIND_META) as ObjectKind[];
  const groups = kindOrder
    .map((kind) => ({ kind, items: edges.filter((e) => e.kind === kind) }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="related-objects-chips" aria-label="Related objects">
      {groups.map(({ kind, items }) => (
        <div className="related-objects-group" key={kind}>
          <h3 className="related-objects-group-title">
            {pluralize(KIND_META[kind].label)} · {items.length}
          </h3>
          <div className="related-objects-group-chips">
            {items.map((edge) => (
              <ObjectChip key={`${edge.kind}:${edge.id}`} kind={edge.kind} id={edge.id} relation={edge.relation} onClick={onNavigate} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
